import {type ReactNode, useState} from 'react';
import {TableCell, TableRow, Stack, Typography, Tooltip, IconButton, alpha} from '@mui/material';
import {FormSelectionEnum, SWRKeyEnum} from 'interfaces';
import {mutate} from 'swr';
import {EMPTY_ID} from 'hooks/swr';
import {styled} from '@mui/material/styles';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {useTranslation} from 'react-i18next';
import DeleteAlert from 'components/alerts/DeleteAlert';
import {EditFolderNameDialog} from './EditFolderNameDialog';
import Alert from 'helpers/Alert';
import backendCommunicator from 'services/backendCommunicator';

export const FolderRow = ({children, name, folderId}: {children?: ReactNode; name: string; folderId?: string}) => {
  const {t} = useTranslation('common');
  const [expanded, setExpanded] = useState<boolean>(false);
  const [display, setDisplay] = useState<boolean>(false);
  const [openDeleteAlert, setOpenDeleteAlert] = useState<boolean>(false);
  const [openEditFolderNameDialog, setOpenEditFolderNameDialog] = useState<boolean>(false);
  const handleDialogOpen = () => {
    setOpenEditFolderNameDialog(true);
  };
  const handleDialogClose = () => {
    setOpenEditFolderNameDialog(false);
  };

  const createFolder = (name: string) => {
    folderId &&
      backendCommunicator
        .updateFolder(folderId, name)
        .then(() => {
          void mutate([EMPTY_ID, SWRKeyEnum.GET_PLAN_FOLDERS]);
        })
        .catch((error) => {
          Alert.error(error);
        });
  };

  return (
    <>
      {openDeleteAlert && (
        <DeleteAlert
          open={openDeleteAlert}
          setOpen={setOpenDeleteAlert}
          id={folderId ?? ''}
          type={FormSelectionEnum.folder}
          handleCommunicationSucces={() => {
            void mutate([EMPTY_ID, SWRKeyEnum.GET_PLAN_FOLDERS]);
          }}
        />
      )}
      {openEditFolderNameDialog && (
        <EditFolderNameDialog
          open={openEditFolderNameDialog}
          currentName={name}
          handleCreate={(name) => {
            createFolder(name);
          }}
          handleClose={handleDialogClose}
        />
      )}
      <TableRow
        hover
        onMouseEnter={() => {
          setDisplay(true);
        }}
        onMouseLeave={() => {
          setDisplay(false);
        }}
        style={{cursor: 'pointer'}}
      >
        <StyledTableCell colSpan={8} display={display}>
          <Stack spacing={1.3} direction={'row'} alignItems={'center'} flexGrow={1}>
            {expanded ? (
              <Stack
                spacing={1}
                p={'6px 16px'}
                direction={'row'}
                alignItems={'center'}
                flexGrow={1}
                onClick={() => {
                  setExpanded(!expanded);
                }}
              >
                <KeyboardArrowUpIcon />
                <FolderOpenIcon fontSize="small" />
                <Typography sx={{userSelect: 'none'}}>{String(name)}</Typography>
              </Stack>
            ) : (
              <Stack
                spacing={1}
                p={'6px 16px'}
                direction={'row'}
                alignItems={'center'}
                flexGrow={1}
                onClick={() => {
                  setExpanded(!expanded);
                }}
              >
                <KeyboardArrowDownIcon />
                <FolderIcon fontSize="small" />
                <Typography sx={{userSelect: 'none'}}>{String(name)}</Typography>
              </Stack>
            )}
            {display && (
              <Stack
                spacing={1.3}
                direction={'row'}
                alignItems={'center'}
                justifyContent={'right'}
                width={50}
                paddingRight={2}
              >
                <Tooltip title={t('edit') ?? ''}>
                  <IconButton
                    sx={{padding: 0}}
                    onClick={handleDialogOpen}
                    data-testid="plans-table-edit-button"
                    size="small"
                    color="secondary"
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('delete') ?? ''}>
                  <IconButton
                    sx={{padding: 0}}
                    onClick={() => {
                      setOpenDeleteAlert(true);
                    }}
                    data-testid="plans-table-delete-button"
                    size="small"
                    color="secondary"
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        </StyledTableCell>
      </TableRow>
      {expanded && <>{children}</>}
    </>
  );
};

interface StyledTableCellProps {
  display: boolean | undefined;
}

const StyledTableCell = styled(TableCell)<StyledTableCellProps>(({theme, display}) => ({
  padding: 0,
  backgroundColor: display
    ? alpha(theme.palette.action.hover, theme.palette.action.hoverOpacity / 4)
    : theme.palette.background.default,
}));
